import React from "react";
import moment from "moment";
import axios from "axios";
import { cargarReactPDF } from "../ventas/reportePickingPDF";
import { crearDocumentoNotaEntregaRespaldo } from "./reporteNotaEntregaRespaldoPDF";

/*
 * Reporte profesional de auditoria en PDF (react-pdf).
 *
 * Usa el mismo bundle que el reporte de Picking: public/lib/reactpdf.bundle.js
 * (ver tools/reactpdf/README.md), cargado con cargarReactPDF().
 *
 * opciones = {
 *   tipo,        'log' | 'historica' | 'eliminados'
 *   registros,   arreglo con los registros consultados
 *   filtros,     filtros aplicados en pantalla (tabla, accion, desde, hasta, ...)
 *   detalle,     (opcional) resumen del registro: { titulo, subtitulo, campos, cambios, bloques }
 *   comparacion, (opcional) solo en "Datos Eliminados": respaldo de la nota de
 *                entrega con que se compara. Cuando viene informada, el reporte
 *                se genera con el documento dedicado de nota de entrega (ver
 *                reporteNotaEntregaRespaldoPDF.js); si no, el reporte generico
 *                de auditoria.
 *   metadatos,   { empresa, rif, usuario, urlLogo }
 * }
 *
 * Si registros tiene un solo elemento se imprime el detalle del registro
 * (datos, campos modificados y JSON); con varios, un listado con las columnas
 * de la pestaña.
 */

const TIPOS = {
  log: {
    titulo: "REPORTE DE AUDITORÍA",
    subtitulo: "Auditoría LOG (tabla audit_log)",
    archivo: "Auditoria-LOG",
  },
  historica: {
    titulo: "REPORTE DE AUDITORÍA",
    subtitulo: "Auditoría (tabla auditoria)",
    archivo: "Auditoria",
  },
  eliminados: {
    titulo: "REPORTE DE DOCUMENTOS ELIMINADOS",
    subtitulo: "Datos Eliminados (tabla auditadataeliminada)",
    archivo: "Datos-Eliminados",
  },
};

const texto = (valor) => {
  if (valor === undefined || valor === null) return "";
  if (valor instanceof Date) return moment(valor).format("DD-MM-YYYY hh:mm a");
  if (typeof valor === "object") {
    try {
      return JSON.stringify(valor);
    } catch (error) {
      return "";
    }
  }
  return String(valor);
};

const jsonTexto = (valor) => {
  if (valor === null || valor === undefined || valor === "") return "";
  try {
    return typeof valor === "string"
      ? JSON.stringify(JSON.parse(valor), null, 2)
      : JSON.stringify(valor, null, 2);
  } catch (error) {
    return String(valor);
  }
};

/* Descarga el logo de la organizacion para incrustarlo en el PDF (si falla,
 * el reporte se genera igual sin logo). */
const cargarLogo = async (url) => {
  if (!url) return null;
  try {
    const respuesta = await axios.get(url, { responseType: "arraybuffer" });
    const bytes = new Uint8Array(respuesta.data);
    let binario = "";
    for (let i = 0; i < bytes.length; i++) {
      binario += String.fromCharCode(bytes[i]);
    }
    const tipo = (respuesta.headers && respuesta.headers["content-type"]) || "image/png";
    return "data:" + tipo + ";base64," + window.btoa(binario);
  } catch (error) {
    return null;
  }
};

/* Columnas del listado cuando el reporte lleva varios registros */
const columnasListado = (tipo) => {
  if (tipo === "eliminados") {
    return [
      { label: "FECHA", flex: 1.6, valor: (item) => item.fecha_ve ? moment(item.fecha_ve).format("DD-MM-YYYY hh:mm a") : "" },
      { label: "DOCUMENTO", flex: 1.6, valor: (item) => texto(item.numdocumento) },
      { label: "STATUS", flex: 1.2, valor: (item) => texto(item.status) },
      { label: "USUARIO", flex: 1.2, valor: (item) => texto(item.idusuario) },
      { label: "REG. AUDITORÍA", flex: 1.2, valor: (item) => texto(item.idauditoria) },
    ];
  }
  return [
    { label: "FECHA", flex: 1.6, valor: (item) => item.fecha_ve ? moment(item.fecha_ve).format("DD-MM-YYYY hh:mm a") : "" },
    { label: "ACCIÓN", flex: 1, valor: (item) => texto(item.accion) },
    { label: "TABLA", flex: 1.6, valor: (item) => texto(tipo === "log" ? item.tabla_afectada : item.nombretabla) },
    { label: "REGISTRO", flex: 1.4, valor: (item) => texto(tipo === "log" ? item.registro_id : item.idtabla) },
    { label: "USUARIO", flex: 1.6, valor: (item) => texto(tipo === "log" ? item.usuario_id : item.usuario) },
    { label: "DETALLE", flex: 2.4, valor: (item) => texto(tipo === "log" ? (item.campos_cambiados || []).join(", ") : item.concepto) },
  ];
};

const crearEstilos = (StyleSheet) =>
  StyleSheet.create({
    pagina: {
      paddingTop: 76,
      paddingBottom: 40,
      paddingHorizontal: 24,
      fontSize: 8,
      fontFamily: "Helvetica",
      color: "#232323",
    },
    encabezado: {
      position: "absolute",
      top: 16,
      left: 24,
      right: 24,
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "flex-end",
      borderBottomWidth: 1.5,
      borderBottomColor: "#0b3d63",
      paddingBottom: 5,
    },
    encabezadoIzq: {
      flexDirection: "row",
      alignItems: "center",
    },
    logo: {
      width: 46,
      height: 46,
      marginRight: 8,
      objectFit: "contain",
    },
    titulo: {
      fontSize: 14,
      fontFamily: "Helvetica-Bold",
      color: "#0b3d63",
    },
    subtitulo: {
      fontSize: 8,
      color: "#4d4d4d",
    },
    resumen: {
      borderWidth: 1,
      borderColor: "#d5dee6",
      backgroundColor: "#f2f6fa",
      paddingVertical: 5,
      paddingHorizontal: 6,
      marginBottom: 10,
    },
    resumenTitulo: {
      fontSize: 8,
      fontFamily: "Helvetica-Bold",
      color: "#0b3d63",
      marginBottom: 3,
    },
    resumenFila: {
      flexDirection: "row",
      flexWrap: "wrap",
    },
    meta: {
      fontSize: 7.5,
      color: "#4d4d4d",
      marginRight: 12,
    },
    seccion: {
      marginBottom: 10,
    },
    seccionTitulo: {
      fontSize: 8.5,
      fontFamily: "Helvetica-Bold",
      color: "#0b3d63",
      borderBottomWidth: 0.8,
      borderBottomColor: "#d5dee6",
      paddingBottom: 2,
      marginBottom: 4,
    },
    datos: {
      flexDirection: "row",
      flexWrap: "wrap",
    },
    dato: {
      width: "50%",
      paddingRight: 10,
      marginBottom: 4,
    },
    datoLabel: {
      fontSize: 6.5,
      color: "#6c757d",
    },
    datoValor: {
      fontSize: 8,
      fontFamily: "Helvetica-Bold",
      color: "#232323",
    },
    filaTitulos: {
      flexDirection: "row",
      backgroundColor: "#0b3d63",
    },
    tituloColumna: {
      fontSize: 7.5,
      fontFamily: "Helvetica-Bold",
      color: "#ffffff",
      paddingVertical: 4,
      paddingHorizontal: 3,
    },
    fila: {
      flexDirection: "row",
      borderBottomWidth: 0.5,
      borderBottomColor: "#dfe6ec",
    },
    filaPar: {
      backgroundColor: "#f7f9fb",
    },
    celda: {
      fontSize: 7.5,
      paddingVertical: 3,
      paddingHorizontal: 3,
    },
    negrita: {
      fontFamily: "Helvetica-Bold",
    },
    anterior: {
      color: "#b00020",
    },
    nuevo: {
      color: "#0b6b3a",
    },
    json: {
      fontSize: 6.5,
      color: "#333333",
      backgroundColor: "#f7f9fb",
      borderWidth: 0.5,
      borderColor: "#d5dee6",
      padding: 4,
    },
    firmas: {
      flexDirection: "row",
      justifyContent: "space-between",
      marginTop: 30,
    },
    firma: {
      width: "30%",
      borderTopWidth: 0.8,
      borderTopColor: "#8a99a8",
      paddingTop: 3,
    },
    firmaTexto: {
      fontSize: 7,
      color: "#5c6b7a",
      textAlign: "center",
    },
    pieFijo: {
      position: "absolute",
      bottom: 16,
      left: 24,
      right: 24,
      flexDirection: "row",
      justifyContent: "space-between",
      borderTopWidth: 0.5,
      borderTopColor: "#d5dee6",
      paddingTop: 3,
    },
    pieTexto: {
      fontSize: 7,
      color: "#6c757d",
    },
    sinDatos: {
      marginTop: 30,
      textAlign: "center",
      fontSize: 10,
      color: "#6c757d",
    },
  });

const metaTexto = (nombre, valor, vacio) =>
  nombre + ": " + (texto(valor) === "" ? vacio || "TODAS" : texto(valor));

/**
 * Documento react-pdf del reporte de auditoria. R = window.ReactPDF
 */
export const crearDocumentoAuditoria = (R, opciones) => {
  const { Document, Page, View, Text, Image } = R;
  const estilos = crearEstilos(R.StyleSheet);

  const datos = opciones || {};
  const tipo = TIPOS[datos.tipo] ? datos.tipo : "log";
  const info = TIPOS[tipo];
  const metadatos = datos.metadatos || {};
  const filtros = datos.filtros || {};
  const lista = Array.isArray(datos.registros) ? datos.registros : [];
  const detalle = lista.length === 1 ? datos.detalle : null;

  const campos = detalle && Array.isArray(detalle.campos) ? detalle.campos : [];
  const cambios = detalle && Array.isArray(detalle.cambios) ? detalle.cambios : [];
  const bloques = (
    detalle && Array.isArray(detalle.bloques) ? detalle.bloques : []
  ).filter((bloque) => jsonTexto(bloque.valor) !== "");
  const columnas = columnasListado(tipo);
  const etiquetas =
    tipo === "eliminados" ? ["DOCUMENTO", "STATUS"] : ["TABLA", "ACCIÓN"];

  return (
    <Document
      title={info.titulo}
      author={texto(metadatos.empresa)}
      subject={info.subtitulo}
      creator="ATHOS ERP"
      producer="ATHOS ERP"
    >
      <Page size="A4" orientation="portrait" style={estilos.pagina}>
        <View style={estilos.encabezado} fixed>
          <View style={estilos.encabezadoIzq}>
            {metadatos.logo ? (
              <Image src={metadatos.logo} style={estilos.logo} />
            ) : null}
            <View>
              <Text style={estilos.titulo}>{info.titulo}</Text>
              <Text style={estilos.subtitulo}>{texto(metadatos.empresa)}</Text>
              {texto(metadatos.rif) === "" ? null : (
                <Text style={estilos.subtitulo}>
                  {"RIF: " + texto(metadatos.rif)}
                </Text>
              )}
            </View>
          </View>
          <View>
            <Text style={estilos.subtitulo}>
              {"Emitido: " + moment().format("DD-MM-YYYY hh:mm a")}
            </Text>
            <Text style={estilos.subtitulo}>
              {"Usuario: " + texto(metadatos.usuario)}
            </Text>
            <Text style={estilos.subtitulo}>{info.subtitulo}</Text>
          </View>
        </View>

        <View style={estilos.resumen}>
          <Text style={estilos.resumenTitulo}>
            {detalle ? "DETALLE DEL REGISTRO" : "LISTADO DE REGISTROS"}
          </Text>
          <View style={estilos.resumenFila}>
            <Text style={estilos.meta}>
              {metaTexto(etiquetas[0], filtros.tabla)}
            </Text>
            <Text style={estilos.meta}>
              {metaTexto(etiquetas[1], filtros.accion)}
            </Text>
            <Text style={estilos.meta}>
              {metaTexto("DESDE", filtros.desde, "-")}
            </Text>
            <Text style={estilos.meta}>
              {metaTexto("HASTA", filtros.hasta, "-")}
            </Text>
            {tipo === "eliminados" ? null : (
              <Text style={estilos.meta}>
                {metaTexto("USUARIO", filtros.usuario)}
              </Text>
            )}
            <Text style={estilos.meta}>{"REGISTROS: " + lista.length}</Text>
          </View>
        </View>

        {lista.length === 0 && (
          <Text style={estilos.sinDatos}>No hay informacion por mostrar</Text>
        )}
        {detalle && (
          <View style={estilos.seccion}>
            <Text style={estilos.seccionTitulo}>
              {texto(detalle.titulo).toUpperCase()}
            </Text>
            <View style={estilos.datos}>
              {campos.map((campo, indice) => (
                <View style={estilos.dato} key={indice} wrap={false}>
                  <Text style={estilos.datoLabel}>
                    {texto(campo.label).toUpperCase()}
                  </Text>
                  <Text style={estilos.datoValor}>{texto(campo.valor)}</Text>
                </View>
              ))}
            </View>
          </View>
        )}

        {cambios.length > 0 && (
          <View style={estilos.seccion}>
            <Text style={estilos.seccionTitulo}>
              {"CAMPOS MODIFICADOS (" + cambios.length + ")"}
            </Text>
            <View style={estilos.filaTitulos} wrap={false}>
              <Text style={[estilos.tituloColumna, { flex: 1.6 }]}>CAMPO</Text>
              <Text style={[estilos.tituloColumna, { flex: 3 }]}>
                VALOR ANTERIOR
              </Text>
              <Text style={[estilos.tituloColumna, { flex: 3 }]}>
                VALOR NUEVO
              </Text>
            </View>
            {cambios.map((cambio, indice) => (
              <View
                key={indice}
                style={[estilos.fila, indice % 2 === 1 ? estilos.filaPar : null]}
                wrap={false}
              >
                <Text style={[estilos.celda, { flex: 1.6 }, estilos.negrita]}>
                  {texto(cambio.campo)}
                </Text>
                <Text style={[estilos.celda, { flex: 3 }, estilos.anterior]}>
                  {texto(cambio.anterior) === "" ? "-" : texto(cambio.anterior)}
                </Text>
                <Text style={[estilos.celda, { flex: 3 }, estilos.nuevo]}>
                  {texto(cambio.nuevo) === "" ? "-" : texto(cambio.nuevo)}
                </Text>
              </View>
            ))}
          </View>
        )}

        {bloques.map((bloque, indice) => (
          <View style={estilos.seccion} key={indice}>
            <Text style={estilos.seccionTitulo}>
              {texto(bloque.label).toUpperCase()}
            </Text>
            <Text style={estilos.json}>{jsonTexto(bloque.valor)}</Text>
          </View>
        ))}

        {!detalle && lista.length > 0 && (
          <View>
            <View style={estilos.filaTitulos} wrap={false}>
              {columnas.map((columna, indice) => (
                <Text
                  key={indice}
                  style={[estilos.tituloColumna, { flex: columna.flex }]}
                >
                  {columna.label}
                </Text>
              ))}
            </View>
            {lista.map((item, indice) => (
              <View
                key={indice}
                style={[estilos.fila, indice % 2 === 1 ? estilos.filaPar : null]}
                wrap={false}
              >
                {columnas.map((columna, posicion) => (
                  <Text
                    key={posicion}
                    style={[estilos.celda, { flex: columna.flex }]}
                  >
                    {columna.valor(item)}
                  </Text>
                ))}
              </View>
            ))}
          </View>
        )}

        <View style={estilos.firmas} wrap={false}>
          <View style={estilos.firma}>
            <Text style={estilos.firmaTexto}>ELABORADO POR</Text>
          </View>
          <View style={estilos.firma}>
            <Text style={estilos.firmaTexto}>REVISADO POR</Text>
          </View>
          <View style={estilos.firma}>
            <Text style={estilos.firmaTexto}>AUTORIZADO POR</Text>
          </View>
        </View>

        <View style={estilos.pieFijo} fixed>
          <Text style={estilos.pieTexto}>
            {"ATHOS ERP | " + info.subtitulo}
          </Text>
          <Text
            style={estilos.pieTexto}
            render={({ pageNumber, totalPages }) =>
              "Pagina " + pageNumber + " de " + totalPages
            }
          />
        </View>
      </Page>
    </Document>
  );
};

/*
 * Abre en blanco la ventana del reporte. Se llama en el clic del boton (no
 * despues) para que el navegador no bloquee la ventana como popup.
 */
export const abrirVentanaReporte = () => {
  const ventana = window.open(
    "",
    "ReporteAuditoria",
    "fullscreen=no,scrollbars=yes,toolbar=no,resizable=no,width=850,height=700"
  );

  if (ventana) {
    try {
      ventana.document.write(
        '<!doctype html><html><head><meta charset="utf-8">' +
          "<title>Reporte de Auditoria</title></head>" +
          '<body style="font-family:Arial,Helvetica,sans-serif;color:#0b3d63;' +
          'text-align:center;margin-top:60px;font-size:15px">' +
          "Generando reporte, espere un momento...</body></html>"
      );
    } catch (error) {
      /* si no se puede escribir se deja la ventana en blanco */
    }
  }

  return ventana;
};

/*
 * Muestra el PDF en la ventana del reporte. El visor de PDF del navegador ya
 * trae su propio boton de impresion, por eso NO se abre el dialogo de
 * impresion automaticamente (asi el reporte sale una sola vez).
 */
const mostrarPdf = (url, ventana) => {
  if (ventana && !ventana.closed) {
    ventana.location.href = url;
    return true;
  }

  const nueva = window.open(
    url,
    "ReporteAuditoria",
    "fullscreen=no,scrollbars=yes,toolbar=no,resizable=no,width=850,height=700"
  );

  return !!(nueva && !nueva.closed);
};

/* Descarga el reporte cuando el navegador no permite abrir la ventana */
const descargarPdf = (url, nombre) => {
  const enlace = document.createElement("a");
  enlace.href = url;
  enlace.download = nombre;
  document.body.appendChild(enlace);
  enlace.click();
  document.body.removeChild(enlace);
};


/**
 * Genera el reporte de auditoria y lo muestra en la ventana indicada.
 * opciones = { tipo, registros, filtros, detalle, metadatos }
 * ventana  = ventana devuelta por abrirVentanaReporte() (opcional)
 */
export const mostrarReporteAuditoria = async (opciones, ventana) => {
  const datos = opciones || {};
  const metadatos = datos.metadatos || {};
  const tipo = TIPOS[datos.tipo] ? datos.tipo : "log";
  const R = await cargarReactPDF();
  const logo = await cargarLogo(metadatos.urlLogo);
  const opcionesDoc = { ...datos, metadatos: { ...metadatos, logo } };
  const comparacion = Array.isArray(datos.comparacion) ? datos.comparacion : [];

  /* Cuando hay respaldo con que comparar (Datos Eliminados) se usa el reporte
   * dedicado de nota de entrega (ver reporteNotaEntregaRespaldoPDF.js); en el
   * resto de los casos, el reporte generico de auditoria. */
  const documento =
    comparacion.length > 0
      ? crearDocumentoNotaEntregaRespaldo(R, opcionesDoc)
      : crearDocumentoAuditoria(R, opcionesDoc);

  const blob = await R.pdf(documento).toBlob();
  const url = URL.createObjectURL(blob);

  if (!mostrarPdf(url, ventana)) {
    descargarPdf(
      url,
      TIPOS[tipo].archivo + "-" + moment().format("DD-MM-YYYY-hhmm") + ".pdf"
    );
  }

  /* el PDF se libera cuando ya no se necesita */
  setTimeout(() => URL.revokeObjectURL(url), 600000);
};






// WEBPACK FOOTER //
// ./src/components/configuracionGeneral/reporteAuditoriaPDF.js